import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Icon from '../../components/Icon'
import { useGoalsStore } from '../../../stores/goalsStore'
import { useStudentsStore } from '../../../stores/studentsStore'
import { useAuth } from '../../../contexts/AuthContext'
import {
  GoalCategory,
  GoalArea,
  CreateGoalRequest,
  UpdateGoalRequest,
  Permission,
} from '../../../types'

// Category to areas mapping (from server Goal model)
const categoryAreaMap: Record<GoalCategory, GoalArea[]> = {
  [GoalCategory.EARLY_LEARNING_SKILLS]: [
    GoalArea.LANGUAGE_AND_LITERACY,
    GoalArea.EARLY_COGNITION_STEAM,
    GoalArea.SOCIAL_EMOTIONAL_DEVELOPMENT,
    GoalArea.PHYSICAL_MOTOR_DEVELOPMENT,
    GoalArea.APPROACHES_TO_LEARNING,
  ],
  [GoalCategory.ACADEMIC]: [
    GoalArea.READING_PHONEMIC_AWARENESS,
    GoalArea.READING_PHONICS,
    GoalArea.READING_FLUENCY,
    GoalArea.READING_VOCABULARY,
    GoalArea.READING_COMPREHENSION,
    GoalArea.MATH_CALCULATION,
    GoalArea.MATH_PROBLEM_SOLVING,
    GoalArea.WRITTEN_LANGUAGE_CONTENT,
    GoalArea.WRITTEN_LANGUAGE_MECHANICS,
    GoalArea.WRITTEN_LANGUAGE_EXPRESSION,
    GoalArea.SPEECH_LANGUAGE_ARTICULATION,
    GoalArea.SPEECH_LANGUAGE_VOICE,
    GoalArea.SPEECH_LANGUAGE_RECEPTIVE,
    GoalArea.SPEECH_LANGUAGE_EXPRESSIVE,
    GoalArea.SPEECH_LANGUAGE_PRAGMATICS,
    GoalArea.COMMUNICATION,
    GoalArea.COGNITIVE,
    GoalArea.PHYSICAL_EDUCATION,
    GoalArea.OTHER,
  ],
  [GoalCategory.SOCIAL_EMOTIONAL_BEHAVIORAL]: [
    GoalArea.SOCIAL_EMOTIONAL_BEHAVIORAL,
    GoalArea.SOCIAL_INTERACTION_SKILLS,
    GoalArea.SELF_MANAGEMENT,
    GoalArea.OTHER,
  ],
  [GoalCategory.HEALTH]: [
    GoalArea.HEALTH_MEDICAL,
    GoalArea.HEARING,
    GoalArea.VISION,
    GoalArea.ORIENTATION_MOBILITY,
    GoalArea.OTHER,
  ],
  [GoalCategory.PHYSICAL]: [
    GoalArea.FINE_MOTOR,
    GoalArea.VISUAL_MOTOR,
    GoalArea.HAND_SKILLS,
    GoalArea.FINE_MOTOR_COORDINATION,
    GoalArea.MANUAL_DEXTERITY,
    GoalArea.BIMANUAL_SKILLS,
    GoalArea.SENSORY_PROCESSING_STRATEGIES,
    GoalArea.GROSS_MOTOR,
    GoalArea.FUNCTIONAL_MOBILITY,
    GoalArea.POSTURAL_CONTROL,
    GoalArea.POSITIONING,
    GoalArea.COMMUNITY_ACCESS,
    GoalArea.INDEPENDENT_LIVING_FEEDING,
    GoalArea.INDEPENDENT_COMMUNITY_LIVING_REC,
    GoalArea.INDEPENDENT_COMMUNITY_LIVING_TOILETING,
    GoalArea.INDEPENDENT_COMMUNITY_LIVING_DRESSING,
    GoalArea.OTHER,
  ],
}

const GoalForm: React.FC = () => {
  const { studentId, goalId } = useParams<{
    studentId?: string
    goalId?: string
  }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { students, fetchStudents } = useStudentsStore()
  const { goals, loading, error, isCreating, isUpdating, addGoal, editGoal } =
    useGoalsStore()

  const [formData, setFormData] = useState({
    description: '',
    category: '' as GoalCategory | '',
    area: '' as GoalArea | '',
    objectives: [''],
  })
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isNewGoal = !goalId
  const currentGoal = !isNewGoal ? goals.find((g) => g._id === goalId) : null
  const currentStudent = students.find((s) => s._id === studentId)

  // Check permissions
  const canManageStudent = user?.permissions.includes(
    Permission.MANAGE_GOALS,
  )

  useEffect(() => {
    if (students.length === 0) {
      fetchStudents()
    }
  }, [students.length, fetchStudents])

  useEffect(() => {
    if (!isNewGoal && currentGoal) {
      setFormData({
        description: currentGoal.description,
        category: currentGoal.category,
        area: currentGoal.area,
        objectives: currentGoal.objectives,
      })
    }
  }, [isNewGoal, currentGoal])

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Reset area if category changes
      ...(name === 'category' && { area: '' }),
    }))
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleObjectiveChange = (index: number, value: string) => {
    const newObjectives = [...formData.objectives]
    newObjectives[index] = value
    setFormData((prev) => ({
      ...prev,
      objectives: newObjectives,
    }))
  }

  const addObjective = () => {
    setFormData((prev) => ({
      ...prev,
      objectives: [...prev.objectives, ''],
    }))
  }

  const removeObjective = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index),
    }))
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.description.trim()) {
      errors.description = 'Goal description is required'
    }
    if (!formData.category) {
      errors.category = 'Goal category is required'
    }
    if (!formData.area) {
      errors.area = 'Goal area is required'
    }
    if (
      formData.objectives.length === 0 ||
      formData.objectives.every((obj) => !obj.trim())
    ) {
      errors.objectives = 'At least one objective is required'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveGoal = async () => {
    if (!validateForm() || !studentId || !canManageStudent) {
      return
    }

    setIsSubmitting(true)

    try {
      const goalData = {
        description: formData.description.trim(),
        category: formData.category as GoalCategory,
        area: formData.area as GoalArea,
        objectives: formData.objectives
          .filter((obj) => obj.trim())
          .map((obj) => obj.trim()),
        student: studentId,
      }

      if (isNewGoal) {
        await addGoal(goalData as CreateGoalRequest)
      } else if (goalId) {
        await editGoal(goalId, goalData as UpdateGoalRequest)
      }

      navigate(`/manage/students/${studentId}/goals`)
    } catch (err) {
      console.error('Error saving goal:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getAvailableAreas = (): GoalArea[] => {
    if (!formData.category) return []
    return categoryAreaMap[formData.category as GoalCategory] || []
  }

  if (loading && students.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!canManageStudent) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          You do not have permission to manage goals
        </div>
        <button
          onClick={() => navigate(`/manage/students/${studentId}/goals`)}
          className="mt-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Goals
        </button>
      </div>
    )
  }

  if (!currentStudent) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Student not found
        </div>
        <button
          onClick={() => navigate('/manage/students')}
          className="mt-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Students
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <button
          onClick={() => navigate(`/manage/students/${studentId}/goals`)}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2 mb-4"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Goals
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNewGoal ? 'Add New Goal' : 'Edit Goal'}
        </h1>
        <p className="text-gray-600 mt-1">
          {currentStudent.firstName} {currentStudent.lastName}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Goal Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter the goal description"
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.description
                ? 'border-red-300'
                : 'border-gray-300'
            }`}
            autoFocus
          />
          {validationErrors.description && (
            <p className="text-red-600 text-sm mt-1">
              {validationErrors.description}
            </p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.category ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a category</option>
            {Object.values(GoalCategory).map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {validationErrors.category && (
            <p className="text-red-600 text-sm mt-1">
              {validationErrors.category}
            </p>
          )}
        </div>

        {/* Area */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Area *
          </label>
          <select
            name="area"
            value={formData.area}
            onChange={handleInputChange}
            disabled={!formData.category}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed ${
              validationErrors.area ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">
              {formData.category ? 'Select an area' : 'Select a category first'}
            </option>
            {getAvailableAreas().map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
          {validationErrors.area && (
            <p className="text-red-600 text-sm mt-1">{validationErrors.area}</p>
          )}
        </div>

        {/* Objectives */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Objectives *
          </label>
          <div className="space-y-2">
            {formData.objectives.map((objective, index) => (
              <div key={index} className="flex gap-2">
                <textarea
                  value={objective}
                  onChange={(e) => handleObjectiveChange(index, e.target.value)}
                  placeholder={`Objective ${index + 1}`}
                  rows={2}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                />
                {formData.objectives.length > 1 && (
                  <button
                    onClick={() => removeObjective(index)}
                    className="px-3 py-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Icon type="trash" className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {validationErrors.objectives && (
            <p className="text-red-600 text-sm mt-1">
              {validationErrors.objectives}
            </p>
          )}
          <button
            onClick={addObjective}
            className="mt-3 px-3 py-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
          >
            <Icon type="plus" className="w-5 h-5" />
            Add Objective
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={() => navigate(`/manage/students/${studentId}/goals`)}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveGoal}
          disabled={isSubmitting || isCreating || isUpdating}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {(isSubmitting || isCreating || isUpdating) && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          {isNewGoal ? 'Create Goal' : 'Update Goal'}
        </button>
      </div>
    </div>
  )
}

export default GoalForm
